import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Bell,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Menu,
  Moon,
  Search,
  Sparkles,
  Sun,
  X,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../context/AuthProvider';
import { useAppearance } from '../../context/AppearanceProvider';
import { ADMIN_NAV, filterAdminNav } from '../../config/adminNavigation';
import { navForRole } from '../../config/navigation';
import { FloatingDock } from '../navigation/FloatingDock';
import { resolveIcon } from '../../lib/iconMap';
import type { AdminNavItem } from '../../types/admin';

function SidebarLink({ item, collapsed }: { item: AdminNavItem; collapsed: boolean }) {
  const Icon = resolveIcon(item.icon);
  if (!item.route) return null;
  return (
    <NavLink
      to={item.route}
      end={item.route === '/admin'}
      className={({ isActive }) => `wfx-admin-nav-link${isActive ? ' is-active' : ''}`}
      title={collapsed ? item.label : undefined}
    >
      <Icon size={18} aria-hidden="true" />
      {!collapsed && <span>{item.label}</span>}
    </NavLink>
  );
}

function SidebarGroup({
  item,
  collapsed,
  isOpen,
  onToggle,
}: {
  item: AdminNavItem;
  collapsed: boolean;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const Icon = resolveIcon(item.icon);
  const location = useLocation();
  const isChildActive = item.children?.some((child) => child.route && location.pathname.startsWith(child.route));

  if (!item.children?.length) {
    return <SidebarLink item={item} collapsed={collapsed} />;
  }

  return (
    <div className={`wfx-admin-nav-group${isChildActive ? ' is-active' : ''}`}>
      <button
        type="button"
        className="wfx-admin-nav-group-btn"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <Icon size={18} aria-hidden="true" />
        {!collapsed && (
          <>
            <span>{item.label}</span>
            {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </>
        )}
      </button>
      {!collapsed && isOpen && (
        <div className="wfx-admin-nav-children">
          {item.children.map((child) => (
            <SidebarLink key={child.id} item={child} collapsed={false} />
          ))}
        </div>
      )}
    </div>
  );
}

export function AdminShell() {
  const { user, logout } = useAuth();
  const { settings, setSettings } = useAppearance();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [openGroupId, setOpenGroupId] = useState<string | null>(null);

  const dockItems = useMemo(() => navForRole(user?.role), [user?.role]);
  const activeDockPath = useMemo(() => {
    const { pathname } = location;
    const matches = dockItems.filter((item) =>
      item.path === '/' ? pathname === '/' : pathname === item.path || pathname.startsWith(`${item.path}/`),
    );
    if (matches.length === 0) return null;
    return matches.reduce((best, item) => (item.path.length > best.path.length ? item : best)).path;
  }, [dockItems, location.pathname]);

  const { data: adminMe } = useQuery({
    queryKey: ['admin-me', user?.id],
    queryFn: async () => {
      try {
        const res = await fetch('/api/admin/me');
        if (res.ok) return await res.json();
      } catch {
        /* fallback to current user context */
      }
      return {
        user: {
          id: user?.id ?? 1,
          name: user?.name ?? 'Admin',
          email: user?.email ?? null,
          role: user?.role ?? 'admin',
        },
        permissions: [
          'dashboard.view',
          'users.manage',
          'roles.manage',
          'content.manage',
          'results.manage',
          'opportunities.manage',
          'media.manage',
          'notifications.manage',
          'seo.manage',
          'analytics.view',
          'support.manage',
          'audit.view',
          'ai.assistant',
          'settings.manage',
          'billing.view',
          'system.health',
        ],
        isSuperAdmin: user?.role === 'admin' || user?.role === 'super_admin',
      };
    },
    staleTime: 60_000,
  });

  const navItems = useMemo(
    () =>
      filterAdminNav(
        ADMIN_NAV,
        adminMe?.permissions ?? [],
        adminMe?.isSuperAdmin ?? (user?.role === 'admin' || user?.role === 'super_admin'),
      ),
    [adminMe, user],
  );

  useEffect(() => {
    const activeGroup = navItems.find((group) =>
      group.children?.some((child) => child.route && location.pathname.startsWith(child.route))
    );
    if (activeGroup) {
      setOpenGroupId(activeGroup.id);
    }
  }, [location.pathname, navItems]);

  const toggleGroup = (id: string) => {
    setOpenGroupId((prev) => (prev === id ? null : id));
  };

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const breadcrumb = location.pathname.startsWith('/cms')
    ? location.pathname.replace('/cms', 'CMS').replace(/\//g, ' / ')
    : location.pathname.replace('/admin', 'Admin').replace(/\//g, ' / ') || 'Admin / Dashboard';

  const handleSidebarToggle = () => {
    if (window.innerWidth < 900) {
      setSidebarOpen((v) => !v);
    } else {
      setCollapsed((v) => !v);
    }
  };

  return (
    <div className={`wfx-admin-shell${collapsed ? ' is-collapsed' : ''}${adminMe?.isSuperAdmin ? ' is-super-admin' : ''}`}>
      <aside className={`wfx-admin-sidebar glass-surface${sidebarOpen ? ' is-open' : ''}`}>
        <div className="wfx-admin-brand">
          <strong>WebFoxx CUET Admin</strong>
          <span className={adminMe?.isSuperAdmin ? 'wfx-admin-role-super' : ''}>
            {adminMe?.isSuperAdmin ? 'Super Admin' : user?.role?.replace('_', ' ')}
          </span>
        </div>
        <nav className="wfx-admin-nav" aria-label="Admin navigation">
          {navItems.map((item) => (
            <SidebarGroup
              key={item.id}
              item={item}
              collapsed={collapsed}
              isOpen={openGroupId === item.id}
              onToggle={() => toggleGroup(item.id)}
            />
          ))}
        </nav>
      </aside>

      {sidebarOpen && (
        <button type="button" className="wfx-admin-backdrop" aria-label="Close menu" onClick={() => setSidebarOpen(false)} />
      )}

      <div className="wfx-admin-main">
        <header className="wfx-admin-header glass-surface">
          <div className="wfx-admin-header-left">
            <button
              type="button"
              className="ghost-icon-button wfx-admin-menu-btn"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open mobile menu"
            >
              <Menu size={20} />
            </button>
            <button
              type="button"
              className="ghost-icon-button wfx-admin-collapse-btn"
              onClick={() => setCollapsed((v) => !v)}
              aria-label="Toggle sidebar collapse"
            >
              {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
            </button>
            <div>
              <p className="wfx-admin-breadcrumb">{breadcrumb}</p>
              <h1 className="wfx-admin-page-title">Command Center</h1>
            </div>
          </div>

          <div className="wfx-admin-search">
            <Search size={16} aria-hidden="true" />
            <input
              type="search"
              placeholder="Search users, tests, questions, pages…"
              aria-label="Global admin search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="wfx-admin-header-actions">
            <button type="button" className="ghost-icon-button" aria-label="Notifications"><Bell size={18} /></button>
            <button
              type="button"
              className="ghost-icon-button"
              aria-label="Toggle theme"
              onClick={() => setSettings({
                theme: settings.theme === 'dark' ? 'light' : 'dark',
              })}
            >
              {settings.theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <button type="button" className="wfx-admin-profile-chip" onClick={logout} title="Sign out">
              <span>{user?.name?.slice(0, 1) ?? 'A'}</span>
              <span>{user?.name ?? 'Admin'}</span>
            </button>
            <button type="button" className="ghost-icon-button wfx-admin-close-mobile" onClick={() => setSidebarOpen(false)} aria-label="Close">
              <X size={18} />
            </button>
          </div>
        </header>

        <main className="wfx-admin-content">
          <Outlet />
        </main>
        <FloatingDock
          items={dockItems}
          activePath={activeDockPath}
          onNavigate={(path) => navigate(path)}
        />
      </div>
    </div>
  );
}
